import axiosInstance from '../helpers/axiosInstance'
import { isTransientHttpError } from '../helpers/retryTransient'
import { isRealDate } from '../utils/warehouseDates'

export const COVERAGE_SLICES = ['covered', 'partial', 'salvaged', 'pending', 'not_stored', 'no_session']

// kind is 'forbidden' (401/403), 'not_found' (404), 'rejected' (422, with the platform's reason) or 'failed'.
// `transient` is true only for a request that failed in transit (no response, or a 5xx). `cause` is the HTTP
// error behind it and is absent when the response itself was malformed.
export class ProofVideoCoverageError extends Error {
  constructor(kind, { reason = null, cause, transient = false } = {}) {
    super(`Proof video coverage ${kind}`)
    this.name = 'ProofVideoCoverageError'
    this.kind = kind
    this.reason = reason
    this.cause = cause
    this.transient = transient
  }
}

const REFUSAL_KINDS = { 401: 'forbidden', 403: 'forbidden', 404: 'not_found' }

// The error kind of a refused request (forbidden or not found), or undefined for any other status.
export const refusalKind = status => REFUSAL_KINDS[status]

const classifyFailure = error => {
  const status = error?.response?.status
  const refusal = refusalKind(status)
  if (refusal) return new ProofVideoCoverageError(refusal, { cause: error })
  if (status === 422) return new ProofVideoCoverageError('rejected', { reason: error.response.data?.reason ?? null, cause: error })
  return new ProofVideoCoverageError('failed', { cause: error, transient: isTransientHttpError(error) })
}

const isCount = value => Number.isInteger(value) && value >= 0

const hasExactSlices = slices =>
  !!slices &&
  typeof slices === 'object' &&
  Object.keys(slices).sort().join() === [...COVERAGE_SLICES].sort().join() &&
  COVERAGE_SLICES.every(slice => isCount(slices[slice]))

// A rate exists exactly when there are packed orders to take it over.
const hasValidRate = ({ packed, covered_rate }) =>
  packed === 0 ? covered_rate === null : Number.isFinite(covered_rate) && covered_rate >= 0 && covered_rate <= 1

export const parseCoverage = data => {
  if (!data || !isCount(data.packed) || !hasExactSlices(data.slices) || !hasValidRate(data) || !isRealDate(data.from) || !isRealDate(data.to)) {
    throw new ProofVideoCoverageError('failed')
  }
  return data
}

export const fetchProofVideoCoverage = async (warehouseId, { from, to }) => {
  let response
  try {
    response = await axiosInstance.get(`/api/v1/warehouses/${warehouseId}/proof-video-coverage`, { params: { from, to } })
  } catch (error) {
    throw classifyFailure(error)
  }
  return parseCoverage(response?.data)
}
